import React, {type ComponentPropsWithoutRef, useState, ChangeEvent, useMemo} from 'react';
import {gql} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {
  EzButton,
  EzRadio,
  EzIcon,
  EzModal,
  EzRadioGroup,
  EzFormControlLabel,
  EzLayout,
  EzSearchInput,
  EzFormLayout,
  EzAlert,
} from '@ezcater/recipe';
import styled from '@emotion/styled';
import {faPlus} from '@fortawesome/free-solid-svg-icons';
import {DmDriverOptInStatus} from '../../../__generated__/globalTypes';
import formattedOrderNumber from '../../../util/formattedOrderNumber';
import type {DeliveryManagementAssignDriverModalDriverFragment} from '../../../__generated__/DeliveryManagementAssignDriverModalDriverFragment';
import useDebouncedValue from '../../../util/useDebouncedValue';
import Loader from '../../Loader';

const toNormalizedSearchKey = (input?: string | null): string => {
  return (input || '').toLowerCase().replace(/[^\w\s]/gi, '');
};

const getPreparedDriversList = (
  drivers?: DeliveryManagementAssignDriverModalDriverFragment[]
): (DeliveryManagementAssignDriverModalDriverFragment & {fullName: string})[] => {
  const formattedList = (drivers || []).map(driver => {
    return {
      ...driver,
      fullName: driver.fullName || '',
      makeModel: driver.makeModel?.trim(),
    };
  });

  return formattedList.sort((a, b) => {
    return a.fullName.localeCompare(b.fullName);
  });
};

const StyledModalBody = styled.div`
  min-height: 315px;
`;

type AssignDriverModalProps = {
  driverId?: string;
  errors?: string[];
  isOpen?: boolean;
  onDismiss: () => void;
  onDriverChange: (driverId: string) => void;
  onDriverCreateModalOpen: () => void;
  onSubmit: (payload: {driverId: string}) => void;
  orderNumber: string;
  potentialDrivers?: DeliveryManagementAssignDriverModalDriverFragment[];
  isLoading?: boolean;
};

const AssignDriverModal = ({
  driverId,
  errors,
  isOpen = false,
  onDismiss,
  onDriverChange,
  onDriverCreateModalOpen,
  onSubmit,
  orderNumber,
  potentialDrivers,
  isLoading = false,
}: AssignDriverModalProps) => {
  const [t] = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 200);
  const driverOptions = useMemo(() => {
    const options = getPreparedDriversList(potentialDrivers);

    if (debouncedSearch) {
      const normalizedInput = toNormalizedSearchKey(debouncedSearch).trim();

      return options.filter(driver =>
        toNormalizedSearchKey(driver.fullName).includes(normalizedInput)
      );
    }

    return options;
  }, [potentialDrivers, debouncedSearch]);

  const shouldShowSearchField = !!potentialDrivers && potentialDrivers.length > 4;
  const selectedDriverId = driverId || driverOptions[0]?.id;

  const getRadioButtonHelperText = (driver: DeliveryManagementAssignDriverModalDriverFragment) => {
    const helperText: string[] = driver.makeModel ? [driver.makeModel] : [];

    if (driver.smsOptInStatus === DmDriverOptInStatus.OPTED_OUT) {
      helperText.push(
        t('components.deliveryManagement.assignDriver.assignDriverModal.driverSmsOptedOut')
      );
    }

    if (driver.smsOptInStatus === DmDriverOptInStatus.NO_OPT_IN_DECISION) {
      helperText.push(
        t('components.deliveryManagement.assignDriver.assignDriverModal.driverSmsOptInPending')
      );
    }

    return helperText.join(', ') || undefined;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      if (selectedDriverId) {
        await onSubmit({driverId: selectedDriverId});
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    onDriverChange(event.target.value);
  };

  const handleModalDismiss = () => {
    onDismiss();
  };

  const handleAddDriverButtonClick = () => {
    onDriverCreateModalOpen();
  };

  const modalConditionalProps: Partial<ComponentPropsWithoutRef<typeof EzModal>> = isLoading
    ? {}
    : {
        submitLabel: t('components.deliveryManagement.assignDriver.assignDriverModal.submitLabel'),
        dismissLabel: t(
          'components.deliveryManagement.assignDriver.assignDriverModal.dismissLabel'
        ),
      };

  return (
    <EzModal
      {...modalConditionalProps}
      isOpen={isOpen}
      onDismiss={handleModalDismiss}
      onSubmit={handleSubmit}
      headerText={t('components.deliveryManagement.assignDriver.assignDriverModal.headerText', {
        orderNumber: formattedOrderNumber(orderNumber),
      })}
      isSubmitting={isSubmitting}
    >
      <StyledModalBody>
        {isLoading ? (
          <Loader inline={false} />
        ) : (
          <EzFormLayout>
            <EzLayout layout="stack">
              {shouldShowSearchField && (
                <EzSearchInput
                  placeholder={t(
                    'components.deliveryManagement.assignDriver.assignDriverModal.searchPlaceholder'
                  )}
                  aria-label={t(
                    'components.deliveryManagement.assignDriver.assignDriverModal.searchLabel'
                  )}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              )}
              <EzRadioGroup
                ariaLabel="Select driver"
                name="driverId"
                onChange={handleRadioChange}
                value={selectedDriverId}
              >
                {driverOptions.map(driver => (
                  <EzFormControlLabel
                    key={driver.id}
                    label={driver.fullName}
                    control={<EzRadio size="small" />}
                    helperText={getRadioButtonHelperText(driver)}
                    value={driver.id}
                  />
                ))}
              </EzRadioGroup>
            </EzLayout>
            <div>
              <EzButton
                legacy
                use="tertiary"
                icon={<EzIcon icon={faPlus} size="small" />}
                onClick={handleAddDriverButtonClick}
              >
                {t('components.deliveryManagement.assignDriver.assignDriverModal.addDriverButton')}
              </EzButton>
            </div>
          </EzFormLayout>
        )}
        {!!errors?.length && (
          <EzAlert
            use="error"
            headline={t(
              'components.deliveryManagement.assignDriver.assignDriverModal.errorHeadline'
            )}
            tagline={errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          />
        )}
      </StyledModalBody>
    </EzModal>
  );
};

AssignDriverModal.fragments = {
  driver: gql`
    fragment DeliveryManagementAssignDriverModalDriverFragment on DmDriver {
      id
      fullName
      smsOptInStatus
      makeModel
    }
  `,
};

export default AssignDriverModal;
